import Button from "../components/Button";
import Icon from "../components/Icon";
import renderWithLineBreaks from "../utils/textUtils";

/**
 * ConfirmationModal
 *
 * @param {object} props
 * @param {string} props.title - Modal title.
 * @param {string} props.description - Modal description with line breaks(\n).
 * @param {boolean} props.isOpen - Whether the modal is visible.
 * @param {function} props.onCancel - Called when user cancels.
 * @param {function} props.onConfirm - Called when user confirms the cancellation.
 * @param {string} [props.confirmButtonText] - Confirm button text.
 * @param {string} [props.confirmButtonHeirarchy] - Confirm button color.
 * @param {string} [props.cancelButtonText] - Cancel button text.
 * @param {string} [props.iconName] - Icon name.
 * @param {string} [props.iconColor] - Icon color.
 * @param {string} [props.iconBackgroundColor] - Icon background color.
 *
 * Example usage:
 * ```jsx
 * <ConfirmationModal
 *   title={"ลบข้อมูล"}
 *   description={"คุณแน่ใจหรือไม่ \n ว่าต้องการลบข้อมูลนี้?"}
 *   isOpen={open}
 *   onCancel={() => setOpen(false)}
 *   onConfirm={() => {
 *     console.log("Confirmed");
 *     setOpen(false);
 *   }}
 * />
 * ```
 */
export default function ConfirmationModal({
    title,
    description,
    isOpen,
    onCancel,
    onConfirm,
    confirmButtonText,
    confirmButtonHeirarchy,
    cancelButtonText,
    iconName,
    iconColor,
    iconBackgroundColor,
    titleAlignment = "center",
    descriptionAlignment = "center"
}) {
    if (!isOpen) return null;
    const iconClassName = `w-[48px] h-[48px] rounded-lg p-2 ${iconBackgroundColor} flex items-center justify-center`;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center modal-scroll z-50">
            <div className="flex flex-col w-[390px] rounded-xl py-6 px-5 gap-7 bg-main-background-system-default">
                <div className="flex flex-col items-center gap-5">
                    <div className={iconClassName}>
                        <Icon name={iconName} size="xl" className={iconColor} />
                    </div>
                    <div className="flex flex-col gap-3 w-full">
                        <span className={`font-semibold text-title text-main-typography-system-default text-${titleAlignment}`}>
                            {title}
                        </span>
                        <p className={`text-body-base text-main-typography-system-description text-${descriptionAlignment}`}>
                            {renderWithLineBreaks(description)}
                        </p>
                    </div>
                </div>
                <div className="flex gap-3">
                    <Button
                        id="cancel"
                        hierarchy="secondary"
                        size="md"
                        text={cancelButtonText || "ยกเลิก"}
                        className="flex-1"
                        onClick={onCancel}
                    />
                    <Button
                        id="confirm"
                        hierarchy={confirmButtonHeirarchy}
                        size="md"
                        text={confirmButtonText || "ยืนยัน"}
                        className="flex-1"
                        onClick={onConfirm}
                    />
                </div>
            </div>
        </div>
    );
}
